/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  styled,
  SvgIconTypeMap,
  TextField,
  Typography,
  CircularProgress,
  Avatar,
  OutlinedInput
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import { TSocialMedia } from 'src/models/general';
import { IObject } from 'src/models/objectManagement';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  IObjectInput,
  ISocialProfileAutoComplete,
  TObjectType
} from 'src/models/objectManagement';
import useOnboarding from 'src/services/useOnboarding';
import { reducerUpdateSocialProfileAutoComplete } from 'src/features/objectManagement';
import Confirmation from '../Confirmation';
import GetSocialMediaIcon from '../GetSocialMediaIcon';
import { getSymbolByStreamType } from 'src/utils/getSymbolByStreamType';
import TiktokIcon from '../TikTokIcon/TikTokIcon';
import { useApolloClient } from '@apollo/client';
import {
  GET_OBJECTS_WITH_ENTITY,
  GET_OBJECTS_WITH_STATUS
} from 'src/services/graphql/objectManagement/query';
import ThreadsIcon from '../Threads/ThreadsIcon';
import LinkedinIcon from '@mui/icons-material/LinkedIn';
import { isIncludeUrlComponent } from 'src/utils/isIncludeUrlComponent';
import { isValidURL } from 'src/utils/isValidURL';
import { getSocmedDomain } from 'src/utils/getSocmedDomain';
import useUsers from 'src/services/useUsers';
import TokopediaIcon from '../TokopediaIcon';
import ShopeeIcon from '../ShopeeIcon';
import BlibliIcon from '../BlibliIcon';
import OlxIcon from '../OlxIcon';
import LazadaIcon from '../LazadaIcon';
import { useLocation } from 'react-router';
import { ECOMMERCE_GET_OBJECTS } from 'src/services/graphql/ecommerce/query';

interface ISocialMedia {
  id: TSocialMedia;
  label: string;
  icon:
    | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
        muiName: string;
      })
    | any;
}

interface Props {
  objectType?: TObjectType;
  width?: number;
  onClose?: () => void;
}

const CustomButton = styled(Button)(
  ({ theme }) => `
    border: unset;
      border-radius: 6px 0 0 6px;
      background-color: ${theme.palette.grey[200]};
      border: 1px solid ${theme.palette.divider};
      border-color: ${theme.palette.common.black}
      color: ${theme.palette.primary.main};
      &:hover{
        background-color: ${theme.palette.grey[300]};
      }

    `
);

const AutoCompleteCustom = styled(Autocomplete)(
  ({ theme }) => `
      .MuiOutlinedInput-root {
        padding: 4px!important;
        border-radius: 0px 6px 6px 0px;
      }

      .MuiAutocomplete-clearIndicator {
        display: none;
      }
      .MuiAutocomplete-inputRoot.MuiOutlinedInput-root .MuiAutocomplete-endAdornment {
        right: 72px;
      }
    `
);

const ListItemButtonCustom = styled(ListItemButton)(
  ({ theme }) => `
      &:hover {
        background-color: ${theme.palette.primary.light}
      }
    `
);

const listOptionSocialMedia: Array<ISocialMedia> = [
  {
    id: 'twitter',
    label: 'X',
    icon: TwitterIcon
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: FacebookIcon
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: InstagramIcon
  },
  {
    id: 'youtube',
    label: 'Youtube',
    icon: YouTubeIcon
  },
  {
    id: 'tiktok',
    label: 'Tiktok',
    icon: TiktokIcon
  },
  {
    id: 'threads',
    label: 'Threads',
    icon: ThreadsIcon
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: LinkedinIcon
  }
];

const listOptionEcommerce: Array<ISocialMedia> = [
  {
    id: 'tokopedia',
    label: 'Tokopedia',
    icon: TokopediaIcon
  },
  {
    id: 'shopee',
    label: 'Shopee',
    icon: ShopeeIcon
  },
  {
    id: 'blibli',
    label: 'Blibli',
    icon: BlibliIcon
  },
  // {
  //   id: 'olx',
  //   label: 'OLX',
  //   icon: OlxIcon
  // },
  {
    id: 'lazada',
    label: 'Lazada',
    icon: LazadaIcon
  }
  // {
  //   id: 'tiktokshop',
  //   label: 'TikTok Shop',
  //   icon: TiktokIcon
  // }
];

const InputSocialProfile: FC<Props> = ({
  objectType = 'account',
  onClose,
  width
}) => {
  const theme = useTheme();
  const client = useApolloClient();
  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );
  const objectListWithStatus = useAppSelector(
    (state) => state.objectManagement.objectListWithStatus
  );
  const { isOnTour } = useAppSelector((state) => state.storeProductTour);
  const { pathname } = useLocation();
  const isEcommerce = pathname.includes('ecommerce');

  const [socialMedia, setSocialMedia] = useState<TSocialMedia>(
    isEcommerce ? 'tokopedia' : 'twitter'
  );
  const [openAddConfirmation, setOpenAddConfirmation] =
    useState<boolean>(false);
  const [content, setContent] = useState<string>('');

  const objectListSocialProfile = useMemo((): Array<IObject> => {
    return objectListWithStatus.filter(
      (object) => object.streamType === 'account'
    );
  }, [objectListWithStatus]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleChangeSocialMedia = (socialMediaId: TSocialMedia) => {
    setSocialMedia(socialMediaId);
    handleClose();
  };

  const project = useAppSelector((state) => state.users.project);
  const { createObjects, responseCreateObjects } = useOnboarding();
  const { getObjectList } = useObjectManagement();
  const { getProjectDetailResult } = useUsers();

  const handleSubmit = (objectInput: IObjectInput) => {
    const { streamType, ...rest } = objectInput;
    const payload = {
      projectId: project.id,
      streamType: 'account',
      objectInput: [rest]
    };
    createObjects({
      variables: payload
    }).then(() => {
      getObjectList(project.id).then(() => {
        onClose();
      });
      getProjectDetailResult(project.id);
      client.refetchQueries({
        include: [
          GET_OBJECTS_WITH_ENTITY,
          GET_OBJECTS_WITH_STATUS,
          ECOMMERCE_GET_OBJECTS
        ]
      });
    });
  };

  const handleAdd = () => {
    const formattedContent =
      isIncludeUrlComponent(content) || isValidURL(content)
        ? content
        : getSocmedDomain(socialMedia) + content;
    const tempObjectInput: IObjectInput = {
      content: formattedContent,
      displayPic: '',
      excludes: [],
      extraContent: '',
      includes: [],
      isHashtag: false,
      languages: '',
      objectName: formattedContent,
      socialMedia: socialMedia,
      streamType: objectType
    };
    handleSubmit(tempObjectInput);
  };

  const objectTypeSymbol = useMemo(
    () => getSymbolByStreamType(objectType),
    [objectType]
  );

  const isObjectAlreadyIncluded = (
    socialProfile: string,
    socialMedia: TSocialMedia
  ) => {
    // find if social profile is included in object list
    const formattedSocialProfile =
      isIncludeUrlComponent(socialProfile) || isValidURL(socialProfile)
        ? socialProfile
        : getSocmedDomain(socialMedia) + socialProfile;
    return objectListSocialProfile.some(
      (object) =>
        object.objectName === formattedSocialProfile &&
        object.socialMedia === socialMedia
    );
  };

  const listOptions = isEcommerce ? listOptionEcommerce : listOptionSocialMedia;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        <CustomButton
          variant="contained"
          onClick={!isOnTour ? handleClick : undefined}
        >
          <GetSocialMediaIcon socialMedia={socialMedia} color="primary" />
          <ArrowDropDownIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
        </CustomButton>
        <OutlinedInput
          fullWidth
          placeholder={
            socialMedia === 'youtube' || socialMedia === 'linkedin'
              ? 'Enter your social profile URL here'
              : isEcommerce
              ? 'Enter your store account URL here'
              : 'Enter a username (without @) or your profile URL'
          }
          onChange={
            !isOnTour ? (event) => setContent(event.target.value) : undefined
          }
          value={content}
          sx={{
            borderRadius: 0
          }}
        />
        <Button
          variant="contained"
          sx={{ borderRadius: '0px' }}
          disabled={!content}
          onClick={
            !isOnTour
              ? () =>
                  isObjectAlreadyIncluded(content, socialMedia)
                    ? setOpenAddConfirmation(true)
                    : handleAdd()
              : undefined
          }
        >
          Add
        </Button>
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List>
          {listOptions.map((socialMedia) => (
            <ListItem disablePadding key={socialMedia.id}>
              <ListItemButtonCustom
                onClick={() => handleChangeSocialMedia(socialMedia.id)}
              >
                <ListItemIcon>{<socialMedia.icon />}</ListItemIcon>
                <ListItemText primary={socialMedia.label} />
              </ListItemButtonCustom>
            </ListItem>
          ))}
        </List>
      </Popover>
      <Confirmation
        title={'Add this object?'}
        labelButton={'Add'}
        message={`The ${
          objectType === 'account'
            ? isEcommerce
              ? 'store account'
              : 'social profile'
            : 'keyword/hashtag'
        } you want to add has already been added.`}
        open={openAddConfirmation}
        onClose={() => setOpenAddConfirmation(false)}
        onOk={() => {
          handleAdd();
        }}
      />
    </>
  );
};

export default InputSocialProfile;
